import React from "react";
import { Loader2, Receipt } from "lucide-react";
import { formatDate, formatMoney } from "./format";

const STATUS_STYLES = {
  paid:    { label: "Paid",     cls: "bg-green-50 text-green-700 border-green-200" },
  pending: { label: "Pending",  cls: "bg-amber-50 text-amber-700 border-amber-200" },
  unpaid:  { label: "Unpaid",   cls: "bg-gray-50 text-gray-600 border-gray-200" },
  failed:  { label: "Failed",   cls: "bg-red-50 text-red-700 border-red-200" },
  expired: { label: "Expired",  cls: "bg-gray-50 text-gray-500 border-gray-200" },
};

export const PaymentStatus = ({ status }) => {
  const m = STATUS_STYLES[status] || { label: status || "—", cls: "bg-gray-50 text-gray-600 border-gray-200" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest rounded border ${m.cls}`}>
      {m.label}
    </span>
  );
};

const HistoryTable = ({ history }) => (
  <div className="hidden sm:block">
    <table className="w-full text-sm">
      <thead className="bg-gray-50 text-[10px] font-mono uppercase tracking-widest text-gray-500">
        <tr>
          <th className="text-left px-6 py-3">Date</th>
          <th className="text-left px-6 py-3">Plan</th>
          <th className="text-left px-6 py-3">Cycle</th>
          <th className="text-right px-6 py-3">Amount</th>
          <th className="text-left px-6 py-3">Status</th>
        </tr>
      </thead>
      <tbody>
        {history.map((t) => (
          <tr key={t.session_id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`txn-row-${t.session_id}`}>
            <td className="px-6 py-3 text-gray-700">{formatDate(t.created_at)}</td>
            <td className="px-6 py-3 capitalize">{t.plan || "—"}</td>
            <td className="px-6 py-3 capitalize text-gray-700">{t.interval || "—"}</td>
            <td className="px-6 py-3 text-right tabular-nums font-medium">{formatMoney(t.amount, t.currency)}</td>
            <td className="px-6 py-3">
              <PaymentStatus status={t.payment_status} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const HistoryCards = ({ history }) => (
  <div className="sm:hidden divide-y divide-gray-100">
    {history.map((t) => (
      <div
        key={t.session_id}
        data-testid={`txn-card-${t.session_id}`}
        className="px-4 py-4 flex items-start justify-between gap-3"
      >
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
            {formatDate(t.created_at)}
          </div>
          <div className="mt-1 text-sm font-medium capitalize">
            {t.plan || "—"} <span className="text-gray-500 font-normal">· {t.interval || "—"}</span>
          </div>
          <div className="mt-2">
            <PaymentStatus status={t.payment_status} />
          </div>
        </div>
        <div className="text-right tabular-nums font-semibold shrink-0">
          {formatMoney(t.amount, t.currency)}
        </div>
      </div>
    ))}
  </div>
);

export const PaymentHistory = ({ history, loading }) => (
  <section data-testid="payment-history" className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
    <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Receipt size={16} strokeWidth={1.5} className="text-gray-600" />
        <div>
          <h2 className="font-display text-lg tracking-tight font-medium leading-none">Payment history</h2>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-1">Last 50 transactions</div>
        </div>
      </div>
      <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">{history.length}</span>
    </div>

    {loading ? (
      <div className="px-6 py-10 flex items-center gap-3 text-gray-500">
        <Loader2 size={16} className="animate-spin" />
        <span className="font-mono text-[10px] uppercase tracking-widest">Loading…</span>
      </div>
    ) : history.length === 0 ? (
      <div className="px-6 py-12 text-center">
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400">Empty</div>
        <div className="font-display text-xl tracking-tight font-medium mt-2">No transactions yet.</div>
        <p className="text-sm text-gray-600 mt-2 max-w-md mx-auto">
          When you subscribe to Solo, Pro, or Studio, every Stripe transaction will show up here with the receipt status.
        </p>
      </div>
    ) : (
      <>
        <HistoryTable history={history} />
        <HistoryCards history={history} />
      </>
    )}
  </section>
);
